import api from "./api";

/**
 * @description Servicio para interactuar con el endpoint /diagnosis-catalog.
 * Gestiona el CRUD del maestro de diagnósticos (CIE-10, SNOMED, custom)
 * incluyendo importación masiva y toggle de destacados.
 */
const diagnosisCatalogService = {};

const endpoint = "/diagnosis-catalog";

/**
 * @description Obtiene listado paginado de diagnósticos con filtros
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} [params.searchInput] - Texto de búsqueda (código o descripción)
 * @param {string} [params.classification] - Filtrar por clasificación (cie10, snomed, custom)
 * @param {boolean} [params.isFeatured] - Filtrar solo destacados
 * @param {number} [params.offset] - Offset para paginación
 * @param {number} [params.limit] - Límite de resultados
 * @returns {Promise<{docs: Array, total: number}>} Resultado paginado
 */
diagnosisCatalogService.getAll = function (params) {
  return api.get(endpoint, { params }).then((res) => res.data);
};

/**
 * @description Obtiene un diagnóstico por su ID
 * @param {string} id - ID del diagnóstico
 * @returns {Promise<Object>} Datos del diagnóstico
 */
diagnosisCatalogService.getById = function (id) {
  return api.get(`${endpoint}/${id}`).then((res) => res.data);
};

/**
 * @description Crea un nuevo diagnóstico en el maestro
 * @param {Object} data - Datos del diagnóstico
 * @param {string} data.code - Código (ej. CIE-10)
 * @param {string} data.description - Descripción del diagnóstico
 * @param {string} data.classification - Clasificación (cie10, snomed, custom)
 * @param {string} [data.chapter] - Capítulo
 * @param {string} [data.category] - Categoría
 * @param {boolean} [data.isFeatured] - Si es destacado
 * @returns {Promise<Object>} Diagnóstico creado
 */
diagnosisCatalogService.create = function (data) {
  return api.post(endpoint, data).then((res) => res.data);
};

/**
 * @description Importa diagnósticos en lote
 * @param {Array<Object>} diagnoses - Array de diagnósticos a importar
 * @returns {Promise<{imported: number, updated: number, errors: Array}>} Resultado de la importación
 */
diagnosisCatalogService.bulkImport = function (diagnoses) {
  return api.post(`${endpoint}/bulk`, { diagnoses }).then((res) => res.data);
};

/**
 * @description Actualiza un diagnóstico existente
 * @param {string} id - ID del diagnóstico
 * @param {Object} data - Campos a actualizar
 * @returns {Promise<Object>} Diagnóstico actualizado
 */
diagnosisCatalogService.update = function (id, data) {
  return api.put(`${endpoint}/${id}`, data).then((res) => res.data);
};

/**
 * @description Elimina un diagnóstico
 * @param {string} id - ID del diagnóstico
 * @returns {Promise<void>}
 */
diagnosisCatalogService.delete = function (id) {
  return api.delete(`${endpoint}/${id}`);
};

/**
 * @description Alterna el estado destacado de un diagnóstico
 * @param {string} id - ID del diagnóstico
 * @returns {Promise<Object>} Diagnóstico actualizado
 */
diagnosisCatalogService.toggleFeatured = function (id) {
  return api.put(`${endpoint}/${id}/feature`).then((res) => res.data);
};

export default diagnosisCatalogService;
